import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {MaybeCoreObject} from 'app-core/framework/CoreObjectConstants';
import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ColumnAccessorKey, ColumnConfiguration} from 'nav/expanded/ListingRendererConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import ChipList, {getChipsRowHeight} from 'ui-library/chips/ChipList';
import ErrorMessage from 'ui-library/ErrorMessage';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  StudyablesStudyIdsQuery,
  StudyablesStudyIdsQueryVariables,
} from './__generated/StudyablesStudyIdsQuery';
import {getStudyableItems, isItemStudyable} from './StudyableUtils';

export interface StudyableItemListingExtras {
  itemStudyAttributes?: ItemStudyAttributes;
}

export const STUDYABLES_STUDY_IDS_QUERY = gql`
  query StudyablesStudyIdsQuery($studyableIds: [StrictString!]!) @skipAudit @engComponent(name: STUDIES) {
    studyableItems(ids: $studyableIds) {
      ... on StudyableInterface {
        id
        studies {
          ... on StudyNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type ItemStudyState = QueryResultType.ERROR | QueryResultType.PENDING | QueryResultType.SUCCESS;
export interface ItemStudyAttributes {
  state: ItemStudyState;
  studyIdsByObjectId: Record<string, Array<string>>;
  refetch: () => void;
}

// Undefined is returned when the query is skipped - meaning studies is not enabled or none of the items are studyable
// This is done intentionally to prevent extra rerenders on the ExpandedItemsTable if there are no meaningful study state changes
// See https://github.com/benchling/aurelia/pull/2728 for more details
export function useItemsInStudiesQuery(
  maybeStudyables: Array<MaybeCoreObject>
): ItemStudyAttributes | undefined {
  const studyableIds = BenchlingConfig.STUDIES_ENABLE
    ? getStudyableItems(maybeStudyables).map((studyable) => studyable.id)
    : [];

  const result = useSkippableBenchlingQuery<StudyablesStudyIdsQuery, StudyablesStudyIdsQueryVariables>(
    STUDYABLES_STUDY_IDS_QUERY,
    {
      skip: studyableIds.length === 0,
      variables: {studyableIds},
    }
  );

  const {type: state} = result;
  switch (state) {
    case QueryResultType.SKIPPED:
      return undefined;
    case QueryResultType.SUCCESS:
      return {
        state,
        studyIdsByObjectId: objectFromEntries(
          accessibleObjs(result.data.studyableItems).map((item) => [
            item.id,
            item.studies.map((study) => (isInaccessible(study) ? study.invalidId : study.id)),
          ])
        ),
        refetch: result.refetch,
      };
    default:
      return {state, studyIdsByObjectId: {}, refetch: result.refetch};
  }
}

interface StudiesListingColumnProps {
  state: ItemStudyState;
  studyIds?: Array<string>;
}

export function StudiesListingColumn(props: StudiesListingColumnProps): JSX.Element {
  switch (props.state) {
    case QueryResultType.PENDING:
      return <HorizontalThrobber />;
    case QueryResultType.ERROR:
      return <ErrorMessage error="Failed to load studies" />;
    default:
      return (
        <ChipList
          itemIds={props.studyIds ?? []}
          isVertical={true}
          chipSize={ChipSize.MINI}
          maxChips={CHIP_DISPLAY_LIMIT_TABLE_ROW}
        />
      );
  }
}

export function getStudiesRowContentHeight(
  studyableId: string,
  itemStudyAttributes?: ItemStudyAttributes
): number {
  const numStudies = itemStudyAttributes?.studyIdsByObjectId?.[studyableId]?.length ?? 0;
  if (BenchlingConfig.STUDIES_ENABLE && numStudies > 0) {
    return getChipsRowHeight(numStudies, ChipSize.MINI);
  }
  return TABLE_ROW_BODY_HEIGHT;
}

export function maybeGetStudiesListingColumn<T extends MaybeCoreObject>(
  itemStudyAttributes?: ItemStudyAttributes
): ColumnConfiguration<T> | undefined {
  if (BenchlingConfig.STUDIES_ENABLE && itemStudyAttributes) {
    return {
      column: {
        header: 'Studies',
        accessorKey: ColumnAccessorKey.STUDIES,
        isCollapsible: false,
        isSortable: false,
      },
      render: (item: T) => (
        <StudiesListingColumn
          state={itemStudyAttributes.state}
          studyIds={itemStudyAttributes.studyIdsByObjectId[item.id]}
        />
      ),
      isHidden: (item: T) => !isItemStudyable(item),
    };
  }

  return undefined;
}
